import {
  Case,
  CommunicationLanguage,
  CommunicationStatus,
  CommunicationType,
  ContactLanguages,
  DocumentSubType,
  ObjectTypeEnum,
  ViewCommunication,
  ViewCommunicationItem,
} from 'briefpoint-client';
import Loading from 'components/Loading';
import { useCommunicationApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import useConfig, { BRIDGE_FF, SPANISH_FF } from 'hooks/useConfig';
import useDocumentDetail, { useDocumentFormInterrogatories } from 'hooks/useDocumentDetail';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { getRequestTypeSingular } from 'screens/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils';
import { DocumentDetailClient } from 'services/DocumentService';
import InterrogatoriesList from './InterrogatoriesList';
import useCase from './useCase';
import useClient from './useClient';

export interface IntLi {
  id: number;
  number?: string | null;
  text?: string | null;
}

export function sortCommunications(a: ViewCommunication, b: ViewCommunication) {
  const sortOrder: CommunicationStatus[] = [
    CommunicationStatus.Complete,
    CommunicationStatus.ChangesRequested,
    CommunicationStatus.InProgress,
    CommunicationStatus.Open,
    CommunicationStatus.Pending,
    CommunicationStatus.Sent,
    CommunicationStatus.Unknown,
    CommunicationStatus.Closed,
    CommunicationStatus.Deleted,
  ];
  const indexA = sortOrder.indexOf(a.status ?? CommunicationStatus.Unknown);
  const indexB = sortOrder.indexOf(b.status ?? CommunicationStatus.Unknown);

  if (indexA - indexB === 0) {
    return (
      b.lastActivityDate?.getTime() ??
      b.createdDate!.getTime() - (a.lastActivityDate?.getTime() ?? a.createdDate!.getTime())
    );
  }
  return indexA - indexB;
}

export default function ClientFeedback() {
  const { user, firm, defaultJurisdiction } = useAuth()!;
  const history = useHistory();
  const { documentId } = useParams<{ documentId: string }>();
  const [document] = useDocumentDetail(documentId);
  const formInterrogatories = useDocumentFormInterrogatories(document?.info?.documentSubType);

  const [activeCommunication, setActiveCommunication] = useState<ViewCommunication>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedInterrogatories, setSelectedInterrogatories] = useState<Set<number>>(new Set());
  const [communicationItems, setCommunicationItems] = useState<Record<string, ViewCommunicationItem>>({});

  const communicationApi = useCommunicationApi();

  const { client, updateClientLanguage } = useClient({
    firmId: user?.firmId ?? undefined,
    id: document?.clientId ?? undefined,
  });
  const { caseData } = useCase({ firmId: user?.firmId ?? undefined, id: document?.caseId ?? undefined });

  const [, featureFlags] = useConfig();
  const useBridge = featureFlags()[BRIDGE_FF] ? featureFlags()[BRIDGE_FF](user, firm) : false;
  const canSpanish = featureFlags()[SPANISH_FF] ? featureFlags()[SPANISH_FF](user, firm) : false;
  //todo:gray out when existing communications are in open / in progress for document?

  const interrogatories = useMemo(() => {
    const isFormInterrogatory = document?.info?.documentSubType === DocumentSubType.FormInterrogatorries;
    return (
      document?.interrogatories?.map((interrogatory) => {
        if (isFormInterrogatory) {
          const formInt = formInterrogatories?.find((formInt) => formInt.id === interrogatory.text);
          return {
            id: interrogatory.id,
            number: formInt?.displayName,
            text: formInt?.text,
          };
        }
        return {
          id: interrogatory.id,
          number: interrogatory.number.toString(),
          text: interrogatory.text,
        };
      }) ?? []
    );
  }, [document, formInterrogatories]);

  const isReadOnly = useMemo(
    () =>
      activeCommunication?.status
        ? [
            CommunicationStatus.Open,
            CommunicationStatus.InProgress,
            CommunicationStatus.ChangesRequested,
            CommunicationStatus.Complete,
          ].includes(activeCommunication.status)
        : false,
    [activeCommunication]
  );

  const createCommunication = useCallback(
    async (doc: DocumentDetailClient, cData: Case) => {
      return communicationApi.communicationCreateClientCommunication({
        createClientCommunication: {
          objectType: ObjectTypeEnum.Document,
          objectId: doc.id,
          caseId: cData.id,
          communicationType: CommunicationType.Review,
          status: CommunicationStatus.Pending,
          communicationInfo: {
            title: getRequestTypeSingular(
              doc.info?.documentSubType!,
              defaultJurisdiction.name ?? 'California'
            ),
            caseTitle: cData.title,
            requestedReviewIds: document?.interrogatories.map((i) => i.id) ?? [],
          },
        },
      });
    },
    [communicationApi, defaultJurisdiction.name, document?.interrogatories]
  );

  const getCommunicationItems = useCallback(
    (communication: ViewCommunication) => {
      const communicationItems = (communication?.externalCommunicationItems ?? []).filter(
        (x) => x.createdBy === user?.id
      );
      const communicationItemsById = communicationItems.reduce(
        (acc, item) => {
          acc[item.id!] = item;
          return acc;
        },
        {} as Record<string, ViewCommunicationItem>
      );
      setCommunicationItems(communicationItemsById);
    },
    [user]
  );

  const loadOrCreateCommunication = useCallback(
    async (doc: DocumentDetailClient, cData: Case) => {
      const coms = await communicationApi.communicationGetCommunicationsForObject({
        objectId: doc.id,
        type: ObjectTypeEnum.Document,
      });
      const filteredCommunications = coms
        .filter((c) => c.status !== CommunicationStatus.Closed)
        .sort(sortCommunications);
      let activeComm: ViewCommunication;
      if (!filteredCommunications?.length) {
        activeComm = await createCommunication(doc, cData);
      } else {
        //todo: set readonly here is open or in progress
        activeComm = filteredCommunications[0];
      }
      setActiveCommunication(activeComm);
      setSelectedInterrogatories(new Set(activeComm.communicationInfo?.requestedReviewIds));
      getCommunicationItems(activeComm);
    },
    [communicationApi, createCommunication, getCommunicationItems]
  );

  const translateCommItem = useCallback(
    async (commItemId: string) => {
      if (!canSpanish) return;
      setIsSubmitting(true);
      if (activeCommunication) {
        const result = await communicationApi.communicationTranslateCommunicationItem({
          commId: activeCommunication.id!,
          id: commItemId,
          toLang: CommunicationLanguage.ES, // or another lang
        });
        setCommunicationItems((prevItems) => ({
          ...prevItems,
          [result.id!]: result,
        }));
      }
      setIsSubmitting(false);
    },
    [activeCommunication, communicationApi, canSpanish]
  );

  const saveCommunicationSelections = useCallback(
    async (selections: Set<number>) => {
      if (activeCommunication?.id) {
        setIsSubmitting(true);
        await communicationApi.communicationUpdateCommunication({
          id: activeCommunication.id,
          putClientCommunication: {
            ...activeCommunication,
            communicationInfo: {
              ...activeCommunication.communicationInfo,
              requestedReviewIds: Array.from(selections),
            },
          },
        });
        setIsSubmitting(false);
      }
    },
    [activeCommunication, communicationApi]
  );

  const saveCommunicationItem = useCallback(
    async (intId: string, lang: CommunicationLanguage, val: string, id?: string) => {
      setIsSubmitting(true);
      if (activeCommunication) {
        const item = id
          ? await communicationApi.communicationPutCommunicationItem({
              id: activeCommunication.id!,
              putCommunicationItem: {
                id,
                objectType: ObjectTypeEnum.Interrogatory,
                objectId: intId,
                communication: val,
              },
            })
          : await communicationApi.communicationAddCommunicationItem({
              id: activeCommunication.id!,
              postCommunicationItem: {
                objectType: ObjectTypeEnum.Interrogatory,
                objectId: intId,
                communication: val,
                language: lang,
              },
            });

        setCommunicationItems((prevItems) => ({
          ...prevItems,
          [item.id!]: item,
        }));
        setIsSubmitting(false);

        return item;
      }
    },
    [activeCommunication, communicationApi]
  );

  const toggleSelect = useCallback(
    (id: number | null) => {
      // Toggle all
      let newSet: Set<number>;
      if (id === null) {
        if (selectedInterrogatories.size === 0) {
          newSet = new Set(interrogatories.map((i) => i.id));
        } else {
          newSet = new Set();
        }
      } else {
        // Toggle one
        newSet = new Set(selectedInterrogatories);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
      }
      setSelectedInterrogatories(newSet);
      saveCommunicationSelections(newSet);
    },
    [interrogatories, saveCommunicationSelections, selectedInterrogatories]
  );

  useEffect(() => {
    if (document && caseData) {
      loadOrCreateCommunication(document, caseData);
    }
  }, [caseData, defaultJurisdiction, document, loadOrCreateCommunication]);

  async function handleClickNext() {
    if (activeCommunication?.id && !isSubmitting) {
      history.push(`/documents/${documentId}/client-feedback/${activeCommunication?.id}/confirm-client-info`);
    }
  }

  let questionType = 'Interrogatory';

  switch (document?.info?.documentSubType) {
    case DocumentSubType.SpecialInterrogatorries:
    case DocumentSubType.FormInterrogatorries:
      questionType = 'Interrogatory';
      break;
    case DocumentSubType.RequestsForAdmission:
    case DocumentSubType.RequestsForProduction:
      questionType = 'Request';
      break;
  }

  return (
    <Loading className="mt-3" isLoading={!activeCommunication}>
      <Container className="page-container">
        <div className="mb-4 bread-crumbs">
          <Link to={'/documents'}>Document List</Link> {`> Request Client Response`}
        </div>
        {useBridge && (
          <InterrogatoriesList
            interrogatories={interrogatories}
            selectedInterrogatories={selectedInterrogatories}
            canSimplify={document?.info?.documentSubType !== DocumentSubType.FormInterrogatorries}
            clientLanguage={client?.contactLanguage ?? ContactLanguages.EnglishUs}
            clientName={client?.name ?? ''}
            documentName={document?.name ?? ''}
            communicationItems={communicationItems}
            onClickNext={handleClickNext}
            onSaveCommunicationItem={saveCommunicationItem}
            onSelect={toggleSelect}
            onUpdateLanguage={updateClientLanguage}
            questionType={questionType}
            readOnly={isReadOnly}
            onTranslate={translateCommItem}
            isSubmitting={isSubmitting}
            canSpanish={canSpanish}
          />
        )}
      </Container>
    </Loading>
  );
}
